import { useEffect, useMemo, useRef, useState } from "react";
import {
  Ban,
  Clock3,
  Fingerprint,
  Globe2,
  RefreshCw,
  Search,
  ShieldAlert,
  Trash2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { ConfirmDialog } from "../components/ui";
import { useResource } from "../hooks/use-resource";
import type {
  Paginated,
  SecurityProblemUser,
  SecurityRequestEvent,
} from "../types";
import { formatDateTime, formatNumber } from "../utils";

const USER_PAGE_SIZE = 10;
const EVENT_PAGE_SIZE = 12;

type UserEvents = Paginated<SecurityRequestEvent> & { userId: string | null };

export function SecurityPage() {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [eventPage, setEventPage] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const deleteInFlight = useRef(false);
  const users = useResource(
    () => api.securityUsers({ page, pageSize: USER_PAGE_SIZE, q: query }),
    [page, query],
  );
  const events = useResource<UserEvents>(
    async () => ({
      userId: selectedId,
      ...(selectedId
        ? await api.securityEvents(selectedId, {
            page: eventPage,
            pageSize: EVENT_PAGE_SIZE,
          })
        : emptyEvents()),
    }),
    [selectedId, eventPage],
  );
  const currentEvents =
    events.data?.userId === selectedId &&
    events.data.pagination.page === eventPage
      ? events.data
      : null;
  const selected = useMemo(
    () => users.data?.items.find((item) => item.userId === selectedId) || null,
    [selectedId, users.data],
  );

  useEffect(() => {
    if (!users.data || users.data.pagination.page !== page) return;
    const lastPage = Math.max(1, users.data.pagination.totalPages);
    if (page > lastPage) {
      setPage(lastPage);
      return;
    }
    const items = users.data?.items || [];
    if (!items.length) {
      setSelectedId(null);
      return;
    }
    if (!items.some((item) => item.userId === selectedId)) {
      setSelectedId(items[0].userId);
      setEventPage(1);
    }
  }, [page, selectedId, users.data]);

  useEffect(() => {
    if (events.data?.userId !== selectedId || !events.data) return;
    const lastPage = Math.max(1, events.data.pagination.totalPages);
    if (eventPage > lastPage) setEventPage(lastPage);
  }, [selectedId, eventPage, events.data]);

  const deleteEvent = async (userId: string, event: SecurityRequestEvent) => {
    if (deleteInFlight.current) return;
    deleteInFlight.current = true;
    setDeletingEventId(event.id);
    try {
      await api.deleteSecurityEvent(userId, event.id);
      events.setData((current) => {
        if (
          current?.userId !== userId ||
          !current.items.some((item) => item.id === event.id)
        )
          return current;
        const total = Math.max(0, current.pagination.total - 1);
        return {
          ...current,
          items: current.items.filter((item) => item.id !== event.id),
          pagination: {
            ...current.pagination,
            total,
            totalPages: Math.ceil(total / current.pagination.pageSize),
          },
        };
      });
      toast.success("已删除");
      await Promise.all([users.refresh(), events.refresh()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "删除失败，请重试");
    } finally {
      deleteInFlight.current = false;
      setDeletingEventId(null);
    }
  };

  const deactivate = async () => {
    if (!selected || !selected.accountActive || deactivating) return;
    setDeactivating(true);
    try {
      const result = await api.setUserActivation(selected.userId, false);
      users.setData((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) =>
                item.userId === selected.userId
                  ? {
                      ...item,
                      accountActive: false,
                      accountDeactivatedAt: result.accountDeactivatedAt,
                    }
                  : item,
              ),
            }
          : current,
      );
      toast.success(`账户已停用，已退出 ${result.revokedSessions} 个会话`);
      setConfirming(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "停用失败");
    } finally {
      setDeactivating(false);
    }
  };

  const refresh = async () => {
    await Promise.all([users.refresh(), events.refresh()]);
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="SECURITY"
        title="异常记录"
        desc={
          users.data
            ? `${formatNumber(users.data.pagination.total)} 位用户存在异常记录`
            : "登录与设备校验异常"
        }
        className="rise-in"
        action={
          <ActionButton
            busy={users.refreshing || events.refreshing}
            onClick={() => void refresh()}
          >
            <RefreshCw size={13} />
            刷新
          </ActionButton>
        }
      />

      <form
        className="rise-in rise-in-1 flex max-w-sm items-center gap-2 rounded-full border border-sand-light bg-paper px-4 py-2 text-[13px]"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setQuery(draftQuery.trim());
        }}
      >
        <Search size={14} className="text-ink-faint" />
        <input
          value={draftQuery}
          onChange={(event) => setDraftQuery(event.target.value)}
          placeholder="搜索姓名或学号"
          className="w-full bg-transparent outline-none placeholder:text-ink-faint/70"
        />
      </form>

      {users.error && (
        <ErrorState error={users.error} onRetry={() => void users.refresh()} />
      )}

      <div className="security-review-grid rise-in rise-in-2 grid gap-4 lg:grid-cols-[minmax(270px,0.72fr)_minmax(0,1.6fr)]">
        <Panel className="security-review-panel flex min-h-0 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-sand-light/70 px-5 py-4">
            <p className="text-[12px] font-medium text-ink">问题用户</p>
            <p className="mt-0.5 text-[10.5px] text-ink-faint">
              按最近异常时间排序
            </p>
          </div>
          <div className="min-h-0 flex-1 divide-y divide-sand-light/60 overflow-auto">
            {users.loading && !users.data ? (
              <LoadingState rows={7} />
            ) : users.data?.items.length ? (
              users.data.items.map((user) => (
                <ProblemUserButton
                  key={user.userId}
                  user={user}
                  active={user.userId === selectedId}
                  onClick={() => {
                    setSelectedId(user.userId);
                    setEventPage(1);
                  }}
                />
              ))
            ) : (
              <EmptyState
                title="没有异常用户"
                description="暂无登录或设备校验异常。"
              />
            )}
          </div>
          {users.data && (
            <Pagination
              page={users.data.pagination.page}
              pages={users.data.pagination.totalPages}
              total={users.data.pagination.total}
              onChange={setPage}
              className="shrink-0 border-t border-sand-light/70 px-4 py-3"
            />
          )}
        </Panel>

        <Panel className="security-review-panel flex min-h-0 min-w-0 flex-col overflow-hidden">
          {selected ? (
            <>
              <div className="shrink-0 flex flex-wrap items-start gap-3 border-b border-sand-light/70 px-5 py-4 sm:px-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-clay-soft text-clay-deep">
                  <ShieldAlert size={18} />
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <strong className="text-[14px] font-medium text-ink">
                      {selected.displayName}
                    </strong>
                    <Tag tone={selected.accountActive ? "danger" : "neutral"}>
                      {selected.accountActive ? "待处理" : "已停用"}
                    </Tag>
                  </div>
                  <p className="mt-0.5 font-mono text-[11px] text-ink-faint">
                    {selected.studentNumber || "—"}
                  </p>
                </div>
                {selected.accountActive && (
                  <ActionButton
                    tone="danger"
                    className="ml-auto"
                    onClick={() => setConfirming(true)}
                  >
                    <Ban size={13} />
                    停用账户
                  </ActionButton>
                )}
              </div>

              <div className="shrink-0 grid grid-cols-2 gap-px border-b border-sand-light/70 bg-sand-light/70 sm:grid-cols-4">
                <Metric label="异常记录" value={selected.requestCount} />
                <Metric label="事件分组" value={selected.eventGroupCount} />
                <Metric label="来源 IP" value={selected.uniqueIpCount} />
                <Metric
                  label="最近发生"
                  value={formatDateTime(selected.lastSeenAt, true)}
                  text
                />
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {events.error && (
                  <div className="p-4">
                    <ErrorState
                      error={events.error}
                      onRetry={() => void events.refresh()}
                    />
                  </div>
                )}
                {!currentEvents && !events.error ? (
                  <LoadingState rows={8} />
                ) : currentEvents?.items.length ? (
                  <div className="divide-y divide-sand-light/60">
                    {currentEvents.items.map((event) => (
                      <SecurityEventRow
                        key={event.id}
                        event={event}
                        deleting={deletingEventId === event.id}
                        deleteDisabled={deletingEventId !== null}
                        onDelete={() =>
                          void deleteEvent(selected.userId, event)
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState title="没有请求明细" description="暂无异常记录" />
                )}
              </div>
              {currentEvents && currentEvents.items.length > 0 && (
                <Pagination
                  page={currentEvents.pagination.page}
                  pages={currentEvents.pagination.totalPages}
                  total={currentEvents.pagination.total}
                  onChange={setEventPage}
                  className="shrink-0 border-t border-sand-light/70 px-5 py-4"
                />
              )}
            </>
          ) : (
            <EmptyState
              title="选择问题用户"
              description="从左侧选择用户后查看具体时间、请求和来源 IP。"
            />
          )}
        </Panel>
      </div>

      <ConfirmDialog
        open={confirming}
        title={`停用 ${selected?.displayName || "该用户"}`}
        description="停用后会立即撤销该用户的全部登录会话。安全事件记录会继续保留。"
        confirmLabel="确认停用"
        danger
        busy={deactivating}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void deactivate()}
      />
    </div>
  );
}

function ProblemUserButton({
  user,
  active,
  onClick,
}: {
  user: SecurityProblemUser;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`block w-full px-5 py-4 text-left transition ${active ? "bg-peach" : "bg-paper hover:bg-cream/70"}`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${active ? "bg-clay text-paper" : "bg-cream-deep text-ink-soft"}`}
        >
          <UserRound size={14} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <strong className="truncate text-[13px] font-medium text-ink">
              {user.displayName}
            </strong>
            {!user.accountActive && <Tag tone="neutral">已停用</Tag>}
          </div>
          <p className="mt-0.5 font-mono text-[10.5px] text-ink-faint">
            {user.studentNumber || "—"}
          </p>
          <div className="mt-2 flex items-center justify-between gap-2 text-[10.5px] text-ink-faint">
            <span>{formatNumber(user.requestCount)} 次异常</span>
            <span>{formatDateTime(user.lastSeenAt, true)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function Metric({
  label,
  value,
  text = false,
}: {
  label: string;
  value: number | string;
  text?: boolean;
}) {
  return (
    <div className="bg-paper px-4 py-3.5">
      <p className="text-[10px] text-ink-faint">{label}</p>
      <strong
        className={`mt-1 block font-medium text-ink ${text ? "text-[11px]" : "font-mono text-[16px]"}`}
      >
        {typeof value === "number" ? formatNumber(value) : value}
      </strong>
    </div>
  );
}

function SecurityEventRow({
  event,
  deleting,
  deleteDisabled,
  onDelete,
}: {
  event: SecurityRequestEvent;
  deleting: boolean;
  deleteDisabled: boolean;
  onDelete: () => void;
}) {
  const reason = reasonMeta(event.reasonCode);
  return (
    <article className="px-5 py-4 sm:px-6">
      <div className="flex flex-wrap items-start gap-2">
        <Tag tone={reason.tone}>{reason.label}</Tag>
        {event.statusCode < 400 && <Tag tone="neutral">已放行</Tag>}
        <span className="rounded-full bg-cream-deep px-2.5 py-0.5 font-mono text-[10.5px] font-medium text-ink-soft">
          {event.method}
        </span>
        {event.occurrenceCount > 1 && (
          <span className="text-[10.5px] text-ink-faint">
            同一分钟 {event.occurrenceCount} 次
          </span>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-3">
          <time className="whitespace-nowrap font-mono text-[10.5px] text-ink-faint">
            {formatDateTime(event.lastSeenAt)}
          </time>
          <ActionButton
            tone="danger"
            busy={deleting}
            disabled={deleteDisabled}
            onClick={onDelete}
            aria-label={`删除此异常（共 ${event.occurrenceCount} 次请求）`}
          >
            <Trash2 size={12} />
            删除
          </ActionButton>
        </div>
      </div>
      <p className="mt-2 break-all font-mono text-[11.5px] leading-relaxed text-ink">
        {event.requestTarget}
      </p>
      <div className="mt-2 grid gap-x-5 gap-y-1.5 text-[10.5px] text-ink-faint sm:grid-cols-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <Globe2 size={11} />
          IP{" "}
          <b className="truncate font-mono font-normal text-ink-soft">
            {event.ipAddress || "未知"}
          </b>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <Fingerprint size={11} />
          会话{" "}
          <b className="truncate font-mono font-normal text-ink-soft">
            {event.sessionFingerprint || "未知"}
          </b>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <ShieldAlert size={11} />
          设备{" "}
          <b className="truncate font-mono font-normal text-ink-soft">
            {event.deviceKeyId || "未绑定"}
          </b>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <Clock3 size={11} />
          首次{" "}
          <b className="truncate font-normal text-ink-soft">
            {formatDateTime(event.firstSeenAt)}
          </b>
        </span>
      </div>
      <details className="mt-2 rounded-lg bg-cream/70 px-3 py-2">
        <summary className="cursor-pointer text-[10.5px] text-ink-soft">
          请求环境
        </summary>
        <dl className="mt-2 grid gap-1 break-all font-mono text-[10px] leading-relaxed text-ink-faint">
          <div>HTTP {event.statusCode}</div>
          <div>Request ID · {event.requestId || "未知"}</div>
          <div>User-Agent · {event.userAgent || "未知"}</div>
          {event.details && <div>{JSON.stringify(event.details)}</div>}
        </dl>
      </details>
    </article>
  );
}

function reasonMeta(code: string): {
  label: string;
  tone: "danger" | "clay";
} {
  const labels: Record<string, string> = {
    LOGIN_WECHAT_CODE_MISSING: "登录未携带微信 code",
    LOGIN_WECHAT_CODE_UNVERIFIED: "登录微信 code 未通过验证",
    DEVICE_ENROLLMENT_REQUIRED: "会话未绑定设备",
    DEVICE_PROOF_REQUIRED: "缺少设备签名",
    DEVICE_KEY_MISMATCH: "设备与会话不匹配",
    DEVICE_KEY_REVOKED: "设备密钥失效",
    DEVICE_TIMESTAMP_INVALID: "客户端时间戳无效",
    DEVICE_TIMESTAMP_OUT_OF_RANGE: "客户端时间偏差过大",
    DEVICE_NONCE_INVALID: "请求随机数无效",
    DEVICE_REPLAY_DETECTED: "检测到请求重放",
    DEVICE_SIGNATURE_INVALID: "签名无效",
    DEVICE_CHALLENGE_INVALID: "旧协议挑战无效或重放",
    DEVICE_CHALLENGE_EXPIRED: "旧协议挑战过期",
    DEVICE_CHALLENGE_MISMATCH: "旧协议请求与挑战不一致",
  };
  return {
    label: labels[code] || code,
    tone:
      code === "LOGIN_WECHAT_CODE_MISSING" ||
      code === "LOGIN_WECHAT_CODE_UNVERIFIED" ||
      code === "DEVICE_TIMESTAMP_OUT_OF_RANGE" ||
      code === "DEVICE_CHALLENGE_EXPIRED"
        ? "clay"
        : "danger",
  };
}

function emptyEvents(): Paginated<SecurityRequestEvent> {
  return {
    items: [],
    pagination: { page: 1, pageSize: EVENT_PAGE_SIZE, total: 0, totalPages: 0 },
  };
}
